import { computed, ref, watch, type Ref } from 'vue';
import {
    getTechData, getTopicData,
} from '@/infrastructure/api-functions';
import { E_ANALYSIS_CHART, E_CHART_TYPE, E_CHART_FIELD, E_TECH_TAB_TYPE } from '@/types';
import { downloadImage } from '@eureka/utils/download-image';
import { useGlobalState } from './use-global-store';
import { generateImgTitle } from '@/infrastructure/utils/generate-img-title';
import { useI18n } from 'vue-i18n';
import { openRightAside } from '@/components/results-drawer/results-drawer';
import { useCompanyBasic } from './use-company-basic';
import _ from 'lodash';
import { hookErrorCode } from '@eureka/services/src/custom-error-handler';
import { LIMITED_PERMISSION_CODE } from '@/constant';
import { formatNumberValue } from '@eureka/utils/number-format';

export function useTechGraph(config: {
    graphTechChart: Ref<{
        generateChartImage: () => Promise<string>;
        drawChart: () => void;
        isChartLoaded: () => boolean;
        clearSelect: () => void;
        isEmptyChart: boolean;
    }>;
}) {
    const { t } = useI18n();

    const { companyId, checkedChild, updateLimitInfo } = useGlobalState();

    const { companyInfo } = useCompanyBasic();

    const loadingTopic = ref(false);

    const tabs = ref([
        {
            name: E_TECH_TAB_TYPE.TECH_FORWARD,
            label: t('forwardCitation'),
        },
        {
            name: E_TECH_TAB_TYPE.TECH_BACKWARD,
            label: t('backwardCitation'),
        }
    ]);

    const techTopic: Ref<
        {
            id: string;
            name: string;
            count: number;
        }[]
    > = ref([]);

    const isShowMore = ref(false);

    const isShowTechTopic = ref(true);

    const isLoadingAllTopic = ref(false)

    const techTopicArray = computed(() => {
        const techTopicArrayInfo = [{ id: '', name: t('all'), count: _.sumBy(techTopic.value, (o) => { return o.count }) }]
        return techTopicArrayInfo.concat(isShowMore.value ? techTopic.value : techTopic.value.slice(0, 10)).map((item) => {
            return {
                ...item,
                count: formatNumberValue(item.count),
            }
        })
    });

    const showMoreText = computed(() => isShowMore.value ? t('MultipleWrapper.collapse') : t('MultipleWrapper.expand'));
    const showMoreIcon = computed(() => isShowMore.value ? 'SolidDoubleArrowUp' : 'SolidDoubleArrowDown');
    const topicId = ref('');
    const activeTab = ref(tabs.value[0].name);

    const title = computed(() => {
        const tab = tabs.value.find((tab) => tab.name === activeTab.value);
        return tab?.label;
    });

    function changeTab(tab: E_TECH_TAB_TYPE) {
        activeTab.value = tab;
    }
    const chartComponent = computed(() => {
        return config.graphTechChart.value;
    });
    const isForward = computed(
        () => activeTab.value === E_TECH_TAB_TYPE.TECH_FORWARD
    );
    const chartKey = computed(() => isForward.value
        ? E_ANALYSIS_CHART.TECH_FORWARD_GRAPH
        : E_ANALYSIS_CHART.TECH_BACKWARD_GRAPH);

    const chartType = E_CHART_TYPE.GRAPH;

    const isFetchLoading = ref(false);

    const isDownloadingImage = ref(false);

    const disableUI = computed(() => {
        return (!chartComponent.value?.isChartLoaded && !isFetchLoading.value) || chartComponent.value?.isEmptyChart;
    });

    function chartConfig() {
        return import('@/constant/chart/graph.json').then((d) => {
            return d.default;
        });
    }

    function getGraphData(
        params: { id: string; topic_id?: string }
    ) {
        const { id, topic_id } = params;
        const getCoDataPromise = getTechData({
            id,
            topic_id,
            chart_type: chartKey.value,
        })
            .finally(() => {
                isFetchLoading.value = false;
                loadingTopic.value = false;
            });
        return hookErrorCode(getCoDataPromise, LIMITED_PERMISSION_CODE, () => {
            updateLimitInfo(true)
        });
    }

    async function getTopicDataInfo() {
        isLoadingAllTopic.value = true
        techTopic.value = await getTopicData({ entity_id: companyId.value, chart_type: chartKey.value })
        isShowTechTopic.value = techTopic.value.length > 0;
        isLoadingAllTopic.value = false
    }

    async function getGraphChartData() {
        isFetchLoading.value = true;
        return getGraphData({ id: companyId.value, topic_id: topicId.value });
    }

    async function downloadChartAsImage() {
        if (isDownloadingImage.value) return;
        isDownloadingImage.value = true;
        try {
            const imageStr = await chartComponent.value?.generateChartImage();
            downloadImage(
                imageStr,
                generateImgTitle(title.value)
            );
        } finally {
            isDownloadingImage.value = false;
        }
    }

    function handleTopic(topic: string) {
        if (topicId.value === topic) return

        topicId.value = topic;

        loadingTopic.value = true;

        chartComponent.value?.drawChart();
    }

    function handleDrillDown(data: Record<string, string>) {
        const currentType = _.get(data, 'item._cfg.type');

        const currentSelectedId = currentType === 'node' ? _.get(data, 'item._cfg.id') : _.get(data, 'item._cfg.model.target');

        if (currentSelectedId === companyInfo.value?.ans_id) return;

        const dimensionList = [
            { field: E_CHART_FIELD.ANCS_ID, value: currentSelectedId }
        ];
        if (topicId.value) {
            dimensionList.push({ field: E_CHART_FIELD.TTC_CLUSTER_LC, value: topicId.value })
        }
        setTimeout(() => {
            openRightAside({
                title: t('patent'),
                data: {
                    company_id: companyId.value,
                    chart_type: chartKey.value,
                    include_children: checkedChild.value,
                    dimension_list: dimensionList,
                },
                onClose: () => {
                    chartComponent.value?.clearSelect?.();
                },
            })
        }, 0);
    }

    function handleMore() {
        isShowMore.value = !isShowMore.value;
    }

    watch(
        () => activeTab.value,
        () => {
            topicId.value = '';
            getTopicDataInfo();
        },
        { immediate: true }
    );

    return {
        topicId,
        tabs,
        activeTab,
        changeTab,
        disableUI,
        isFetchLoading,
        chartType,
        chartKey,
        chartConfig,
        getGraphChartData,
        downloadChartAsImage,
        handleTopic,
        techTopic,
        loadingTopic,
        handleDrillDown,
        handleMore,
        techTopicArray,
        showMoreText,
        showMoreIcon,
        isShowTechTopic,
        isLoadingAllTopic,
    };
}
